import type { IInvitationRepository, IOrganizationRepository } from '@knownow/domain'
import { ValidationError, ConflictError } from '@knownow/domain'

export interface AcceptInvitationInput {
  readonly token: string
  readonly userId: string
}

export class AcceptInvitationUseCase {
  constructor(
    private readonly invitationRepo: IInvitationRepository,
    private readonly orgRepo: IOrganizationRepository,
  ) {}

  async execute(input: AcceptInvitationInput): Promise<{ organizationId: string }> {
    const invitation = await this.invitationRepo.getByToken(input.token)

    if (invitation.status !== 'pending') {
      throw new ValidationError(`Invitation is ${invitation.status}`)
    }

    if (invitation.expiresAt && invitation.expiresAt < new Date()) {
      await this.invitationRepo.updateStatus(invitation.id, 'expired')
      throw new ValidationError('Invitation has expired')
    }

    // Check if already a member
    const members = await this.orgRepo.getMembers(invitation.organizationId)
    if (members.some((m) => m.userId === input.userId)) {
      throw new ConflictError('You are already a member of this organization')
    }

    // Create membership
    await this.orgRepo.addMember({
      userId: input.userId,
      organizationId: invitation.organizationId,
      role: invitation.role,
      createdAt: new Date(),
    })

    // Mark invitation as accepted
    await this.invitationRepo.updateStatus(invitation.id, 'accepted')

    return { organizationId: invitation.organizationId }
  }
}
